import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useThemedLogo } from '../../hooks/useThemedLogo';
import { useSidebarNavigation } from './useSidebarNavigation';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Menu,
  X,
  Factory,
  Settings,
  Users,
  Mail,
  BarChart3,
  Wrench,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { canOpenAdminSetup } from '../../lib/adminSetupAccess';
import { useIsSupplier } from '../../hooks/usePortalMode';
import SidebarNotificationBell from './SidebarNotificationBell';
import SidebarVersion from './SidebarVersion';
import { type NavItem } from '../../config/navigation';
import { claimedByTabItem, tabHrefMatches } from './navTabLink';

/**
 * Main application sidebar with role-aware navigation.
 * Renders customer, supplier, or Barratt-specific navigation items based on the
 * user's platform, portal mode, feature flags, menu visibility settings, and module permissions.
 * Supports collapsible desktop and slide-out mobile layouts.
 */
export default function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarCollapsed: collapsed, toggleSidebar } = useLayout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['Category']); // Category expanded by default
  const { user } = useAuth();
  // CR-8217: a customer admin in Supplier View gets no Admin Setup link.
  const isSupplierView = useIsSupplier();
  const { isFeatureEnabled, isDashboardTabEnabled, isMenuItemVisible, notificationIconSide, isLoading: menuSettingsLoading } = useMenuSettings();

  // GRN Repair access, in the same order as the backend
  // ``_require_repair_access`` gate:
  //   1. CR-6274: the GRN Repair module switch (Platform Admin → Features &
  //      Modules). Not every customer uses the toolbox, so it is switchable
  //      on its own rather than being implied by the Scheduled tab.
  //   2. A Scheduled-enabled install is a precondition for EVERYONE,
  //      superusers included — the toolbox repairs GRN-to-schedule-slot
  //      allocations, so on a tenant with no schedule lines there is nothing
  //      for it to act on. It used to show for superusers regardless.
  //   3. Within a scheduled install, superusers and Django staff both
  //      qualify, so schedule-based tenants can let their buyers repair
  //      allocations without handing out superuser.
  const canRepairGrn = isFeatureEnabled('grn_repair')
    && isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);
  const { branding } = useBranding();
  // CR-6243: header logo, else the company logo for the active theme.
  const { appLogo } = useThemedLogo();

  // Which side of the portal to render, the ordered entries for it and the
  // visibility / permission filtering all live in useSidebarNavigation —
  // shared with NavigationAccessGate (CR-7058) so a page hidden here is the
  // page the route gate refuses. Role flags come from the same place:
  // "VC internal" is decided by platform / company_type, never by Django's
  // is_staff flag (CR-5367), and canManageUsers is
  // isVCInternal || is_company_admin || can_manage_users.
  const {
    navigation: configuredNavigation, isVCInternal, isCustomerLikeUser, canManageUsers,
  } = useSidebarNavigation();
  let navigation = configuredNavigation;

  // Supplier sidebar gating:
  //   * Pre-approval (supplier_is_approved === false): only show My Onboarding.
  //   * Post-approval: hide My Onboarding — their questionnaires live under
  //     the regular Questionnaires menu, so the onboarding page is no longer
  //     relevant and becomes dead real estate if kept.
  const isSupplierUser = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  const supplierApproved = user?.supplier_is_approved;
  // CR-5919: Pre-approval suppliers must not reach the dashboard via the
  // platform-name header. Point the header link at the onboarding page for
  // them; everyone else continues to land on /dashboard.
  const isPreApprovalSupplier = isSupplierUser && supplierApproved === false
    && isMenuItemVisible('onboarding');
  const headerHref = isPreApprovalSupplier ? '/onboarding/my-progress' : '/dashboard';
  // Pre-approval (is_approved=false): only the onboarding wizard is useful.
  // Post-approval: the My Onboarding nav entry becomes dead real estate —
  // hide it. But other items that reuse /onboarding/my-progress as their
  // href (e.g. Questionnaires, which lists the supplier's FilledDatasets)
  // must survive this filter, so we match by name — not href.
  // CR-8252: only while the tenant has compliance (isPreApprovalSupplier
  // includes the switch) — otherwise My Onboarding is not in the menu at all
  // and forcing it would leave the sidebar empty.
  if (isPreApprovalSupplier) {
    navigation = navigation.filter(item => item.name === 'My Onboarding');
  } else if (isSupplierUser && supplierApproved === true) {
    navigation = navigation.filter(item => item.name !== 'My Onboarding');
  }

  const isActive = (href: string) => {
    // CR-8613: a tab's own item (Call Off) owns that tab of its page.
    if (href.includes('?')) return tabHrefMatches(href, pathname, location.search);
    if (claimedByTabItem(href, navigation.map(i => i.href), pathname, location.search)) return false;
    // Check for exact match first
    if (pathname === href) return true;

    // Special handling for /suppliers vs /suppliers/performance
    // Don't let /suppliers match if we're on /suppliers/performance
    if (href === '/suppliers' && pathname.startsWith('/suppliers/performance')) {
      return false;
    }

    // Special handling for /admin vs /admin/* sub-routes
    // Don't let /admin match if we're on any admin sub-route
    if (href === '/admin' && pathname.startsWith('/admin/')) {
      return false;
    }

    // Check if current path is a sub-route of the nav item
    // But only if it's actually a sub-route (has a / after the base path)
    if (href !== '/dashboard' && href !== '/') {
      // Check if pathname starts with href followed by a slash
      if (pathname.startsWith(href + '/')) {
        return true;
      }
    }

    return false;
  };

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const NavLink = ({ item, isChild = false }: { item: NavItem; isChild?: boolean }) => {
    const active = isActive(item.href);
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);

    // For items with children, render expandable section
    if (hasChildren && !collapsed) {
      return (
        <div>
          {/* Parent item with expand toggle */}
          <div
            className={cn(
              'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group cursor-pointer',
              active
                ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30',
            )}
            onClick={() => toggleExpanded(item.name)}
          >
            <Icon className={cn(
              'h-5 w-5 flex-shrink-0 transition-all duration-200 mr-3',
              active ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
            )} />
            <span className={cn(
              "font-medium transition-all duration-200 flex-1",
              active ? "text-primary font-semibold" : "group-hover:text-primary/70"
            )}>
              {item.name}
            </span>
            {/* Expand/Collapse chevron */}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {/* Children items */}
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-2">
              {/* Link to parent page */}
              <Link
                to={item.href}
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-1.5 text-sm rounded-md transition-all duration-200 group',
                  isActive(item.href)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className="text-xs">View All</span>
              </Link>
              {item.children!.map((child) => (
                <NavLink key={child.name} item={child} isChild={true} />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Standard link rendering
    return (
      <Link
        to={item.href}
        data-tour={item.tourId}
        onClick={() => {
          if (window.innerWidth < 768) {
            setMobileMenuOpen(false);
          }
        }}
        className={cn(
          'flex items-center text-sm font-medium rounded-md transition-all duration-200 relative group',
          isChild ? 'px-3 py-1.5' : 'px-3 py-2',
          active
            ? isChild
              ? 'bg-primary/10 text-primary font-medium'
              : 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
            : isChild
              ? 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30',
        )}
      >
        <Icon className={cn(
          'flex-shrink-0 transition-all duration-200',
          isChild ? 'h-4 w-4' : 'h-5 w-5',
          collapsed ? 'mr-0' : 'mr-3',
          active ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
        )} />
        {!collapsed && (
          <span className={cn(
            "transition-all duration-200",
            isChild ? "text-sm" : "font-medium",
            active ? "text-primary font-semibold" : "group-hover:text-primary/70"
          )}>
            {item.name}
          </span>
        )}
        {/* Active indicator dot */}
        {active && !collapsed && !isChild && (
          <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-card shadow-lg border border-border hover:bg-accent transition-colors"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        data-tour="sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 ease-in-out',
          'md:translate-x-0',
          collapsed ? 'md:w-20' : 'md:w-64',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col border-r border-border bg-card/95 backdrop-blur-sm">
          {/* Header */}
          <div className="flex h-16 items-center justify-between gap-2 px-4 border-b border-border">
            <Link to={headerHref} className="flex items-center min-w-0 flex-1">
              {/* CR-6243: the dedicated header logo wins when one is uploaded
                  (the sign-in logo is often too tall for a 64px bar); failing
                  that, the company logo for the active theme. Both unset
                  leaves the icon + name pair below untouched. */}
              {appLogo ? (
                <img
                  src={appLogo}
                  alt={branding.companyName}
                  className="h-10 max-w-[160px] object-contain flex-shrink-0"
                />
              ) : (
                <>
                  <Factory className="h-8 w-8 text-primary flex-shrink-0" />
                  {/* CR-5881: long platform names must not be cut off. Drop the
                      single-line `truncate`; instead wrap to at most two lines
                      (leading-tight keeps both inside the h-16 header) with a
                      native title tooltip as a safety-net for extreme lengths. */}
                  <span
                    title={branding.companyName}
                    className={cn(
                      "ml-2 min-w-0 text-lg font-bold leading-tight line-clamp-2 break-words",
                      collapsed && "hidden md:hidden"
                    )}
                  >
                    {branding.companyName}
                  </span>
                </>
              )}
            </Link>
            <button
              type="button"
              className="hidden md:flex flex-shrink-0 p-1 rounded-md hover:bg-accent"
              onClick={toggleSidebar}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
            {/* Notifications bell — pinned above the nav items when the admin
                has enabled the sidebar placement. Independent of the top-bar
                bell (both allowed). */}
            {notificationIconSide && (
              <div className="mb-1 pb-2 border-b border-border">
                <SidebarNotificationBell
                  collapsed={collapsed}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </div>
            )}
            {menuSettingsLoading ? (
              /* Skeleton placeholder to prevent nav flicker during settings load */
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="flex items-center px-3 py-2 gap-3">
                    <div className="h-5 w-5 bg-muted rounded flex-shrink-0" />
                    {!collapsed && <div className="h-4 bg-muted rounded flex-1" />}
                  </div>
                ))}
              </div>
            ) : (
              navigation.map((item) => (
                <NavLink key={item.name} item={item} />
              ))
            )}
          </nav>

          {/* Footer */}
          <div className="border-t border-border p-3 space-y-1">
            {/* Admin Setup — tenant configuration: VC internal OR a customer
                admin (lib/adminSetupAccess, shared with the page's own gate). */}
            {canOpenAdminSetup(user, isSupplierView) && (
              <Link
                to="/admin/setup"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/setup')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Settings className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/setup') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/setup') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Admin Setup
                  </span>
                )}
              </Link>
            )}

            {/* Platform Admin — VC internal only. CR-5367 round 4: the
                ``|| user?.is_superuser`` escape hatch this gate previously
                carried let supplier accounts through whenever their stale
                Django ``is_superuser`` flag survived from earlier test
                data (Sarah's 2026-05-20 regression repro). The flag is
                orthogonal to tenant scoping — exactly the rule
                security.access.get_effective_company_type spells out — so
                the gate now reads platform/company_type only. Helix admins
                set is_superuser=True via the rebuilt Super Admin toggle
                still match isVCInternal because their company_type='helix'. */}
            {isVCInternal && (
              <Link
                to="/admin/platform"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/platform')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Settings className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/platform') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/platform') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Platform Admin
                  </span>
                )}
              </Link>
            )}

            {/* Platform Analytics — VC internal, plus staff-flagged customer
                admins (customer request 2026-07-09): on one-app-per-tenant
                installs the analytics only cover the admin's own tenant.
                Platform Admin / Email Queue stay VC-internal only. */}
            {(isVCInternal || (isCustomerLikeUser && user?.is_staff && user?.is_company_admin)) && (
              <Link
                to="/admin/platform-analytics"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/platform-analytics')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <BarChart3 className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/platform-analytics') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/platform-analytics') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Platform Analytics
                  </span>
                )}
              </Link>
            )}

            {/* Settings */}
            <Link
              to="/settings"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setMobileMenuOpen(false);
                }
              }}
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                isActive('/settings')
                  ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
              )}
            >
              <Settings className={cn(
                'h-5 w-5 flex-shrink-0 transition-all duration-200',
                collapsed ? 'mr-0' : 'mr-3',
                isActive('/settings') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
              )} />
              {!collapsed && (
                <span className={cn(
                  "font-medium transition-all duration-200",
                  isActive('/settings') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                )}>
                  My Account
                </span>
              )}
              {isActive('/settings') && !collapsed && (
                <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
              )}
            </Link>

            {/* CR-6273: the global "Login As" sidebar entry has been removed.
                Impersonation is now reachable only from User Management →
                User details → Login As, so it is always scoped to the user the
                admin is actually inspecting (company/site/role context). */}

            {/* User Management — any admin with the user-management
                capability (Helix internal, customer admin, OR supplier
                admin). Non-admin suppliers stay hidden.

                CR-5367 round 4 narrowed this to "VC internal OR
                customer-like admin", which over-corrected and stripped
                Supplier Admins (e.g. Jacqueline Miller @ Ironvale
                Precision UK) of access — they need to manage users
                within their own company. The earlier (round 3) backend
                scope hardening already restricts non-Helix callers to
                their own company in UserProfileViewSet, so widening the
                nav gate is safe: a supplier admin can only ever see
                their own company's users.

                Round 5 (this change): gate on ``canManageUsers``, which
                is ``isVCInternal || isCompanyAdmin || userCanManageUsers``.
                The bare ``is_superuser`` flag is no longer in the
                composition, so legacy supplier accounts with a stale
                Django flag still can't slip through. */}
            {canManageUsers && !isPreApprovalSupplier && (
              <Link
                to="/admin/users"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/users')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Users className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/users') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/users') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    User Management
                  </span>
                )}
                {/* Active indicator dot */}
                {isActive('/admin/users') && !collapsed && (
                  <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
                )}
              </Link>
            )}

            {/* GRN Allocation Repair — superuser-only surgical tool for
                fixing GRNs the import pipeline left on the wrong schedule
                (typically the synthetic line-0 anchor). Drag-and-drop UI
                + quick action buttons per card; every move atomically
                re-allocates, recomputes on_time_status, reconciles the
                old/new schedule shipped_qty / balance / status, and
                invalidates the dashboard + supplier-performance caches.
                Visible to superusers, and to Django staff on Scheduled
                installs (see ``canRepairGrn``). */}
            {canRepairGrn && (
              <Link
                to="/admin/grn-repair"
                onClick={() => {
                  if (window.innerWidth < 768) setMobileMenuOpen(false);
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/grn-repair')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Wrench className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/grn-repair') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    'font-medium transition-all duration-200',
                    isActive('/admin/grn-repair') ? 'text-primary font-semibold' : 'group-hover:text-primary/70'
                  )}>
                    GRN Repair
                  </span>
                )}
                {isActive('/admin/grn-repair') && !collapsed && (
                  <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
                )}
              </Link>
            )}

            {/* Email Queue — VC internal only (CR-5367 round 4 — see
                Platform Admin comment above for why the is_superuser
                escape hatch was dropped). */}
            {isVCInternal && (
              <Link
                to="/admin/email-queue"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/email-queue')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Mail className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/email-queue') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/email-queue') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Email Queue
                  </span>
                )}
                {/* Active indicator dot */}
                {isActive('/admin/email-queue') && !collapsed && (
                  <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
                )}
              </Link>
            )}

            {/* A supplier still completing onboarding gets only the wizard.
                User Management and Help & Support were still rendered for
                them: the routes redirect, so the links were dead ends. */}
            {!isPreApprovalSupplier && (
            <Link
              to="/help"
              data-tour="help"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setMobileMenuOpen(false);
                }
              }}
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                pathname === '/help'
                  ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
              )}
            >
              <HelpCircle className={cn(
                'h-5 w-5 flex-shrink-0 transition-all duration-200',
                collapsed ? 'mr-0' : 'mr-3',
                pathname === '/help' ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
              )} />
              {!collapsed && (
                <span className={cn(
                  "font-medium transition-all duration-200",
                  pathname === '/help' ? "text-primary font-semibold" : "group-hover:text-primary/70"
                )}>
                  Help & Support
                </span>
              )}
              {/* Active indicator dot for help */}
              {pathname === '/help' && !collapsed && (
                <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
              )}
            </Link>
            )}

            {/* Version display — plain text. The changelog modal it used to
                open is deliberately not wired up. CR-8222: Helix users get the
                deployed build on hover. */}
            <SidebarVersion collapsed={collapsed} showBuild={isVCInternal} />
          </div>
        </div>
      </div>
    </>
  );
}
